import React, { useEffect, useMemo, useRef, useState } from 'react';
import EmptyChatStatus from '../../components/ChatMessenger/EmptyChatStatus';
import ChatList from '../../components/ChatMessenger/ChatList';
import ChatBox from '../../components/ChatMessenger/ChatBox';
import ChatListUser from '../../components/ChatMessenger/ChatListUser';
import useSocket from '../../hooks/useSocket';
import '../../assets/css/chat.css';
import { useSearchParams } from 'react-router-dom';
import {
  fetchBotChatUsers,
  fetchBotUsers,
  fetchChatMessages,
  fetchUserData,
} from '../../api/user';
import SearchFilter from '../../components/analytics-and-insights/SearchFilter';
import { useSelector } from 'react-redux';
import { notificationCounterUpdate } from '../../api/notification'
import { Box, Button } from '@mui/material';
import { InituateIcon } from '../../icons';
import InitiateModal from '../../components/ChatMessenger/bot-profile-chat/InitiateModal';

const ChatApp = () => {
  const filter = useSelector((state) => state.filter);
  const filterWithoutUTM = useMemo(() => {
    const { utm_options, ...rest } = filter;
    return rest;
  }, [JSON.stringify(filter)]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [selectedChatBot, setSelectedChatBot] = useState(null);
  const { socket,botChatUserList, handleUserOnline, handleUserOffline,emitChatFilter} = useSocket();
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [chatData, setChatData] = useState([]);
  const [botUsersLoading, setBotUsersLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  // A failed fetch used to leave chatData/users as [] and be swallowed by a
  // console.error, so the columns rendered the "No Messages Yet?" empty state — a
  // request that 500s and a genuinely empty list looked identical on screen. Tracked
  // so the list can say which one happened and offer a retry.
  const [botUsersError, setBotUsersError] = useState(false);
  const [usersError, setUsersError] = useState(false);
  const [users, setUsers] = useState([]);
  // Both columns open on "Unread Messages": Chat Messenger is worked as a queue, and the
  // unfiltered lists bury the threads that still need an answer under everything already
  // handled. The dashboard's Unread card (which routes in with filter.filterType set)
  // still wins for the bot column, and Reset in either filter modal goes back to "All".
  const [selectedChatFilter, setSelectedChatFilter] = useState('Unread Messages')
  const [selectedBotFilter, setSelectedBotFilter] = useState(filter?.filterType ?? 'Unread Messages')
  const [botSearch, setBotSearch] = useState('')
  const [userSearch, setUserSearch] = useState('')
  const [initialLoad, setInitialLoad] = useState(false)
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  // Deep link into one conversation: /chat?bot=<botId>&member=<memberId>. Messaging
  // Activity links here from a conversation row, so an admin reading "she never replied
  // to this bot" can open the real thread and answer it. Only bot conversations can be
  // linked -- this screen is organised bot-first, so a member-to-member thread has no
  // column to land in.
  const [searchParams] = useSearchParams();
  const linkBot = searchParams.get('bot');
  const linkMember = searchParams.get('member');
  // Which link has already been acted on. Without it the select-the-bot effect would
  // re-run (and yank the selection back) every time the bot list refreshes.
  const linkDoneRef = useRef(null);

  // const { filter } = useSelector((state) => state);
  const botListRef = useRef(null);
  const selectedChatRef = useRef(selectedChat);

  // Chat Messenger has no date control of its own, but the global dashboard filter rides
  // along on every request anyway — and getBotUsers hands startDate/endDate to
  // filterMiddleware, which turns them into a filter on MESSAGE timestamps. With the
  // default 30-day window that hid 47% of the bots (8,193 listed of 15,518 on "All"):
  // every bot whose last activity is older simply is not there, so searching a name that
  // had gone quiet returned an empty list and the search box looked broken (ADMIN-14).
  //
  // The window is kept for a drill-in from the dashboard's UNREAD BOT CHATS card, whose
  // count is date-windowed the same way, so that list still reconciles with the number
  // that was clicked. `statsTitle` is only set by a card navigation — it is the same
  // signal ChatList already uses to retitle the column.
  const botListFilter = useMemo(() => {
    const next = { ...filter, filterType: selectedBotFilter };
    if (!filter?.statsTitle) delete next.dateRange;
    return next;
  }, [filter, selectedBotFilter]);

  const handleChatClick = (chat) => {
    if (chat?.participantId) {
      setUsers(prevUsers => prevUsers.map((user) => {
        if (user._id === chat._id) {
          return {
            ...user, unreadCount: 0
          }
        }
        else {
          return user
        }
      }));
      setSelectedChat(chat);
      if (chat?.unreadCount > 0) {
        notificationCounterUpdate({ userId: selectedChatBot._id, senderId: chat?.participantId?._id, type: 'MESSAGE' })
        setChatData((prevChatData) => {
          const updatedUsers = prevChatData.map((user) => {
            if (user._id === chat.userId) {
              return {
                ...user,
                notificationCounter: user?.notificationCounter ? user?.notificationCounter - chat?.unreadCount : 0,
              };
            }
            return user;
          });
          return updatedUsers
        })
      }
    } else {
      setSelectedChat(null);
    }
  };

  const handleBotChatClick = (chat) => {
    if (socket) {
      handleUserOffline(selectedChatBot?._id)
      handleUserOnline(chat?._id)
    }
    setSelectedChat(null)
    setSelectedChatBot(chat);
    // Switching bots drops back to the default, same as before — only the default moved.
    setSelectedChatFilter("Unread Messages")
  };

  // Routed through the same loading/error states as the list itself. Without them a
  // search gave no feedback while it ran, and a failed one rejected into nothing —
  // leaving the previous term's rows on screen with no indication anything had gone
  // wrong, which is indistinguishable from a search that simply found those rows.
  const handleBotSearch = async () => {
    try {
      setBotUsersLoading(true)
      setBotUsersError(false)
      await fetchBotUsers({ setData: setChatData, searchTerm: botSearch, filter: botListFilter, limit, reset: true })
    } catch (error) {
      console.error('Error while searching bot users', error);
      setBotUsersError(true)
    } finally {
      setBotUsersLoading(false)
    }
  }

  const handleUserSearch = async () => {
    await fetchBotChatsData()
  }

  const limit = 50

  const fetchData = async (reset = false) => {
    try {
      setBotUsersLoading(true)
      setBotUsersError(false)
      await fetchBotUsers({ data: chatData, setData: setChatData, filter: botListFilter, searchTerm: botSearch, page:reset ? 1 :page, limit, reset });
    } catch (error) {
      console.error('Error while botUsersLoading bot users data', error);
      setBotUsersError(true)
    } finally {
      setBotUsersLoading(false)
    }
  };
  const refreshBotListPreservingSelection = async () => {
    try {
      setBotUsersLoading(true)
      setBotUsersError(false)
      await fetchBotUsers({ data: chatData, setData: setChatData, filter: botListFilter, searchTerm: botSearch, page:1, limit, reset: true });
    } catch (error) {
      console.error('Error while refreshing bot users data', error);
      setBotUsersError(true)
    } finally {
      setBotUsersLoading(false)
    }
  };

  const handleScroll = () => {
    const el = botListRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const BOTTOM_THRESHOLD = 50; // px of slack to trigger the next page early
    const bottom = distanceFromBottom <= BOTTOM_THRESHOLD;
    if (bottom && !botUsersLoading) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  useEffect(() => {
    if (socket) {
      const messageResponseHandler = (updatedChat) => {
        const selectedChatOneToOneId = selectedChatRef.current?.OneToOneId;
        if (updatedChat?.type === 'admin') {
          if (selectedChatOneToOneId === updatedChat?.result?.OneToOneId) {
            // if (updatedChat?.result?.schedule) {
            //   setMessages((prevMessages) => [
            //     ...prevMessages,
            //     updatedChat?.result
            //   ]);
            // }
            // else {
             
            //   setMessages((prevMessages) => [
            //     ...prevMessages.filter((message) => message.schedule !== true),
            //     updatedChat?.result,
            //   ]);
            // }
            const updatedMsg = updatedChat?.result;
            setMessages((prevMessages) => {
              const filtered = prevMessages.filter(
                (msg) =>
                  // msg?._id !== updatedMsg?._id &&
                  msg?._id !== updatedMsg?.deletedScheduleMsgId
              );

              const merged = updatedMsg
                ? [...filtered.filter((msg) => msg?._id !== updatedMsg?._id), updatedMsg]
                : filtered;

              const normalMessages = merged.filter(m => !m?.schedule);
              const scheduledMessages = merged
                .filter(m => m?.schedule)
                .sort(
                  (a, b) =>
                    new Date(a?.scheduledDateTime) -
                    new Date(b?.scheduledDateTime)
                );

              return [...normalMessages, ...scheduledMessages];
            });


            setUsers((prevUsers) => {
              return prevUsers.map((user) => {
                if (user.OneToOneId === updatedChat?.result?.OneToOneId && !updatedChat?.result?.schedule) {
                  return {
                    ...user,
                    lastMessage: {
                      isDeleted: updatedChat?.result?.isDeleted,
                      message: updatedChat?.result?.message,
                      messageType: updatedChat?.result?.messageType,
                      timestamp: updatedChat?.result?.timestamp
                    }
                  };
                }
                return user;
              });
            });

          } else {
            const isOneToOneExist = chatData.find((user) => user._id === updatedChat?.result.receiverId);
            if (!isOneToOneExist) {
              refreshBotListPreservingSelection();
            }
            else {
              updateCounterAndLastSeen(updatedChat?.result);
            }
          }
        }
      };

      socket.on('messageResponse', messageResponseHandler);

      return () => {
        socket.off('messageResponse', messageResponseHandler);
      };
    }
  }, [socket, chatData]);

  useEffect(() => {
    setPage(1)
    setSelectedChat(null)
    setSelectedChatBot(null)
    setUsers([])
    fetchData(true)
      .finally(fin => setInitialLoad(true));
  }, [JSON.stringify(filterWithoutUTM),selectedBotFilter]);

  useEffect(() => {
    if ((chatData.length > 0 || page === 1) && initialLoad) {
      fetchData()
    }
  }, [page])

  useEffect(() => {
    const listElement = botListRef.current;
    if (listElement) {
      listElement.addEventListener('scroll', handleScroll);

    }
    return () => {
      if (listElement)
        listElement.removeEventListener('scroll', handleScroll);
    };
  }, [botUsersLoading]);


  useEffect(() => {
    fetchBotChatsData();
    // if (selectedChatBot) {
    //   getRecentChatList(selectedChatBot._id);
    // }
  }, [selectedChatBot]);

  useEffect(() => {
    fetchRoomMessages();
  }, [selectedChat]);

  const fetchBotChatsData = async () => {
    // No bot selected means there is no inbox to load. This ran unguarded on mount (the
    // [selectedChatBot] effect fires with null) and again whenever the filter effect
    // resets the selection, so `userId` serialised as the STRING "undefined" and every
    // Chat page load fired GET /admin/user/bot/undefined -> 500 against production.
    if (!selectedChatBot?._id) {
      setUsers([]);
      setUsersLoading(false);
      // Clear too, or an error from the previously selected bot keeps the error state
      // (and its Retry button) on screen for a column that simply has no bot selected.
      setUsersError(false);
      return;
    }

    try {
      setUsersLoading(true)
      setUsersError(false)
      let sortBy;
      let unRead;

      if (selectedChatFilter === 'New Messages') {
        sortBy = 'lastMessage';
      } else if (selectedChatFilter === 'Unread Messages') {
        unRead = true;
      }

      const params = {
        userId: selectedChatBot?._id,
        setData: setUsers,
        sortOrder: -1,
      };

      if (sortBy) params.sortBy = sortBy;
      if (unRead !== undefined) params.unRead = unRead;
      if (userSearch !== '') params.searchTerm = userSearch

      await fetchBotChatUsers(params);
    } catch (error) {
      console.error('Error while botUsersLoading bot users data', error);
      setUsersError(true)
    } finally {
      setUsersLoading(false)
    }
  };

  // Switching chats used to leave the PREVIOUS conversation on screen: `messages` was
  // only reassigned once the new fetch resolved, so the header changed instantly while
  // the pane still showed the person you had just navigated away from. That is the
  // "lagging on switching the chat" report — it reads as slowness even when the request
  // is quick, because you are looking at visibly wrong content instead of a spinner.
  //
  // The `isCurrent` guard covers a second failure: getRequest keys its aborts on the URL
  // path with the query string STRIPPED (utils/axiosUtils.js), and every chat in one
  // bot's list shares the path /admin/user/messages/<botId> — only ?roomId= differs. So
  // requests for different rooms collide on one key and can resolve out of order; a late
  // reply for the old room would otherwise overwrite the room you are now reading. Only
  // a response that still matches the selected room is applied.
  const fetchRoomMessages = async () => {
    const chat = selectedChat;

    if (!chat?.userId || !chat?.OneToOneId) {
      setMessages([]);
      setMessagesLoading(false);
      return;
    }

    const requestedRoomId = chat.OneToOneId;
    // selectedChatRef is synced in an effect declared ABOVE this one, so it already
    // holds the new chat by the time this runs.
    const isCurrent = () =>
      selectedChatRef.current?.OneToOneId === requestedRoomId;

    setMessages([]);
    setMessagesLoading(true);
    try {
      await fetchChatMessages(chat.userId, requestedRoomId, (msgs) => {
        if (isCurrent()) setMessages(msgs);
      });
    } catch (error) {
      console.error('Error while loading room messages', error);
    } finally {
      // Left alone when stale: the newer request owns the flag and will clear it.
      // On an aborted-but-still-current request this falls through to an empty
      // thread rather than a spinner that never stops.
      if (isCurrent()) setMessagesLoading(false);
    }
  };

  const updateCounterAndLastSeen = (data) => {
    updateUser(data)
    updateBot(data)
  }

  const updateBot = (data) => {
    setChatData((prevChatData) => {
      const updatedUsers = prevChatData.map((user) => {
        if (user._id === data.receiverId) {
          return {
            ...user,
            notificationCounter: user.notificationCounter + 1,
          };
        }
        return user;
      });

      const updatedUserIndex = updatedUsers.findIndex((user) => user._id === data.receiverId);
      if (updatedUserIndex !== -1) {
        const updatedUser = updatedUsers.splice(updatedUserIndex, 1)[0];
        updatedUsers.unshift(updatedUser);
      }

      return updatedUsers;
    });
  };

  const updateUser = (data) => {
    setUsers((prevUsers) => {
      return prevUsers.map((user) => {
        if (user.OneToOneId === data.OneToOneId) {
          return {
            ...user,
            unreadCount: user.unreadCount + 1,
            lastMessage: {
              isDeleted: data.isDeleted,
              message: data.message,
              messageType: data.messageType,
              timestamp: data.timestamp
            }
          };
        }
        return user;
      });
    });
  }

  // Step 1 of the deep link: select the bot. Deferred until the first bot-list load
  // has finished, because that load's effect resets `selectedChatBot` to null and
  // would otherwise wipe a selection made before it.
  //
  // The bot is fetched by id rather than looked up in `chatData`: that list is
  // paginated 50 at a time and filtered, so the linked bot is usually not in it.
  useEffect(() => {
    if (!linkBot || !initialLoad) return undefined;
    const token = `${linkBot}:${linkMember || ''}`;
    if (linkDoneRef.current === token) return undefined;
    linkDoneRef.current = token;
    let active = true;
    (async () => {
      try {
        const bot = await fetchUserData(linkBot, () => {}, false, null, { isAbort: false });
        if (!active || !bot?._id) return;
        handleBotChatClick(bot);
        // handleBotChatClick drops the member column back to "Unread Messages", which
        // is the right default when browsing but wrong here: a conversation being read
        // from a report has almost always been read already, and the row we were sent
        // to would simply not be in the list.
        setSelectedChatFilter('All');
      } catch (error) {
        console.error('Error opening the linked bot conversation', error);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkBot, linkMember, initialLoad]);

  // Step 2: open the member's thread once that bot's inbox has arrived. Guarded on the
  // inbox belonging to the linked bot, so a list still in flight for a previously
  // selected bot can't be searched for a row that is not its own.
  useEffect(() => {
    if (!linkBot || !linkMember || selectedChat) return;
    if (String(selectedChatBot?._id || '') !== String(linkBot)) return;
    if (!users?.length) return;
    const row = users.find((u) => String(u?.participantId?._id || '') === String(linkMember));
    if (row) handleChatClick(row);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, selectedChatBot, selectedChat, linkBot, linkMember]);

  // The bot column renders one PAGE of bots (50 at a time, filtered), and the highlight
  // is the only thing on screen naming whose inbox is open (WEB-48). A bot selected from
  // outside that page therefore had no row at all: the right-hand columns filled with its
  // conversation while the left column looked like nothing was selected, and ChatList's
  // scroll-into-view had no element to find. That is every deep link from Messaging
  // Activity, since the linked bot is almost never on the loaded page.
  //
  // Pinning the selected bot to the top when the list does not contain it keeps the list
  // itself untouched (no refetch, no filter change) and is self-healing: a refresh that
  // replaces `chatData` — an incoming message triggers one — re-pins on the next render
  // instead of silently dropping the highlight again.
  const botListWithSelected = useMemo(() => {
    const list = chatData || [];
    const id = selectedChatBot?._id;
    if (!id) return list;
    return list.some((b) => String(b?._id) === String(id)) ? list : [selectedChatBot, ...list];
  }, [chatData, selectedChatBot]);

  // bot chat user filtering by socket event
  useEffect(() => {
    if (selectedChatFilter && initialLoad) {
      emitChatFilter({ userId: selectedChatBot?._id, selectedFilter: selectedChatFilter,isAdmin:true })
    }
  }, [selectedChatFilter])

  // Only adopt a list that belongs to the bot currently selected. getRecentChatList is
  // debounced server-side, so switching bots while a request is in flight used to drop
  // the previous bot's rows into this column; clicking one then opened that bot's room
  // with this bot's id as the sender, which is what put two conversations on screen at
  // once and rendered every message on the same side (ADMIN-18).
  useEffect(() => {
    if (!botChatUserList?.userId) return;
    if (String(botChatUserList.userId) !== String(selectedChatBot?._id)) return;
    setUsers(botChatUserList.list)
  }, [botChatUserList, selectedChatBot?._id])

  return (
    <div>
      {/* <SearchFilter type={'chat'} /> */}
      {/* char-area */}
      <section className="message-area border-top">
        <div className="container-fluid px-0">
          <div className="row">
            <div className="col-12">
              <Box sx={{ py: 1, display: 'flex', px: 3, justifyContent: 'end', }}><Button onClick={() => setOpen(true)} sx={{ fontSize: '14px', color: '#fff', background: '#BE1F1F', borderRadius: 1, textTransform: 'capitalize', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.7, '&:hover': { background: '#ff4A4A' } }}><InituateIcon /> Initiate Chat</Button></Box>
              <div className="chat-area"
              >
                {/* chatlist */}
                <ChatList
                  ref={botListRef}
                  chatData={botListWithSelected}
                  onChatClick={handleBotChatClick}
                  setSelectedFilter={setSelectedBotFilter}
                  selectedFilter={selectedBotFilter}
                  selectedChat={selectedChatBot}
                  searchTerm={botSearch}
                  setSearchTerm={setBotSearch}
                  handleSearch={handleBotSearch}
                  botUsersLoading={botUsersLoading}
                  handleScroll={handleScroll}
                  handleUserOnline={handleUserOnline}
                  handleUserOffline={handleUserOffline}
                  error={botUsersError}
                  onRetry={() => fetchData(true)}
                />

                {/* {botUsersLoading && <div className="loader">Loading...</div>} */}
                <ChatListUser
                  chatData={users}
                  setUsers={setUsers}
                  selectedChatBot={selectedChatBot}
                  selectedChat={selectedChat}
                  onChatClick={handleChatClick}
                  setSelectedFilter={setSelectedChatFilter}
                  selectedFilter={selectedChatFilter}
                  searchTerm={userSearch}
                  setSearchTerm={setUserSearch}
                  handleSearch={handleUserSearch}
                  loading={usersLoading}
                  error={usersError}
                  onRetry={fetchBotChatsData}
                />
                {/* chatlist */}
                {/* chatbox */}
                {selectedChat ? (
                  <ChatBox
                    chat={selectedChat}
                    messages={messages}
                    messagesLoading={messagesLoading}
                    setMessages={setMessages}
                    senderId={selectedChatBot?._id}
                    receiverId={selectedChat?.participantId?._id}
                    roomId={selectedChat.OneToOneId}
                    updateCounterAndLastSeen={updateCounterAndLastSeen}
                  />
                ) : (
                  <EmptyChatStatus />
                )}
              </div>
              {/* chatbox */}
            </div>
          </div>
        </div>
      </section>
      {/* char-area */}

      {/* Modal Component */}
      <InitiateModal open={open} onClose={() => setOpen(false)} />
    </div>
  );
};

export default ChatApp;
